import type { CreateRoundSnapshotInput } from "../round/round-types.js";
import { RoundCommandError } from "../round/round-errors.js";
import type {
  ImportedPlayerScores,
  ImportedPlayerTotals,
} from "./imported-totals-types.js";

export function validateImportedTotals(input: CreateRoundSnapshotInput): void {
  const totals = input.importedTotals;
  if (
    !totals ||
    totals.version !== 1 ||
    totals.source !== "reviewed-gamebook" ||
    ![9, 18].includes(totals.holeCount) ||
    input.schemaVersion !== 3 ||
    input.rules.kind !== "ggb-import" ||
    !["stroke-play", "stableford"].includes(input.rules.format)
  )
    fail(
      "Only reviewed Stroke Play or Stableford imports can use total-only facts.",
    );
  if (
    input.orderedHoleNumbers?.length !== 0 ||
    input.course.holes.length !== 0 ||
    input.course.teeName !== "" ||
    input.course.rating !== null ||
    input.course.slope !== null ||
    input.participants.some((p) => p.teeSnapshot || p.handicapSnapshot)
  )
    fail(
      "Total-only imports must not manufacture physical holes or tee/handicap allocations.",
    );
  const ids = Object.keys(totals!.players).sort();
  if (
    ids.join() !==
    input.participants
      .map((p) => p.id)
      .sort()
      .join()
  )
    fail("Each imported total must belong to exactly one round participant.");
  for (const player of Object.values(totals!.players)) {
    validateImportedPlayerScores(
      player,
      input.rules.format,
      input.mainContest?.scoringBasis,
    );
    validateContext(player);
  }
}

export function validateImportedPlayerScores(
  player: ImportedPlayerScores,
  format: string,
  basis: string | undefined,
): void {
  if (
    !validNumber(player.gross, 1, 540) ||
    !validNumber(player.net, -100, 540) ||
    !validNumber(player.stableford, 0, 600)
  )
    fail(
      "Imported totals must be actual integer scores, or explicitly unknown.",
    );
  if (format !== "stableford" && player.stableford !== null)
    fail("Stableford points need the original Stableford scoring agreement.");
  const decisive =
    format === "stableford"
      ? player.stableford
      : basis === "gross"
        ? player.gross
        : player.net;
  if (decisive === null || decisive === undefined)
    fail(
      "Enter every player's actual total in the agreed scoring basis. Other totals may stay unknown.",
    );
}

function validateContext(player: ImportedPlayerTotals): void {
  if (
    player.teeName !== null &&
    (typeof player.teeName !== "string" ||
      !player.teeName.trim() ||
      player.teeName.length > 80)
  )
    fail("The reported tee must be known or explicitly unknown.");
  if (!validNumber(player.playingHandicap, -20, 100))
    fail("Reported Playing Handicap must be an integer or unknown.");
}
function validNumber(value: number | null, min: number, max: number): boolean {
  return (
    value === null || (Number.isInteger(value) && value >= min && value <= max)
  );
}
function fail(message: string): never {
  throw new RoundCommandError(message);
}
